"use client";

import { CcHeroLogoBand } from "@/components/home/CcHeroLogoBand";
import { useLocalizedHref } from "@/components/layout/CcLocaleContext";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";
import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { useOpensBookCallPopup } from "@/lib/use-opens-book-call-popup";

const CC_HOME_CLOSING_CTA_TITLE_ID = "cc-home-closing-cta-title";

type CcHomeClosingCtaClientProps = {
  eyebrow: string;
  logoBandCaption: string;
  ui: {
    ctaLabel: string;
    footerTitle: string;
    footerText: string;
  };
  /** Dev inspo inspector key (`inspo-sources.ts`). */
  devSectionKey?: string;
  bookCallVariant?: BookCallPopupVariant;
};

/** Centered primary book-call CTA for overview closing bands (home + dev preview). */
export function CcHomeClosingCtaClient({
  ui,
  eyebrow,
  logoBandCaption,
  devSectionKey = "book-call",
  bookCallVariant,
}: CcHomeClosingCtaClientProps) {
  const contactHref = useLocalizedHref("/contact");
  const opensBookCallPopup = useOpensBookCallPopup(ui.ctaLabel);
  const { ref, withRevealClassName } = useCcSectionReveal();

  return (
    <section
      ref={ref}
      className={withRevealClassName("cc-home-closing-cta")}
      id="get-started"
      aria-labelledby={CC_HOME_CLOSING_CTA_TITLE_ID}
      data-cc-nav="dark"
      {...devSectionAttr(devSectionKey)}
    >
      <div className="cc-home-closing-cta__frame">
        <div className="cc-home-closing-cta__container">
          <div className="cc-home-closing-cta__intro">
            <CcSanjayaEyebrow theme="on-dark">{eyebrow}</CcSanjayaEyebrow>
            <h2 id={CC_HOME_CLOSING_CTA_TITLE_ID} className="cc-home-closing-cta__title">
              {ui.footerTitle}
            </h2>
            <p className="cc-home-closing-cta__subtitle">{ui.footerText}</p>
          </div>

          <div className="cc-home-closing-cta__actions">
            <CcButton
              href={opensBookCallPopup ? undefined : contactHref}
              bookCallPopup={opensBookCallPopup}
              bookCallVariant={bookCallVariant}
              label={ui.ctaLabel}
              variant="primary"
              expand
              expandCompact
              className="cc-home-closing-cta__action cc-home-closing-cta__action--primary"
            />
            <div className="cc-home-closing-cta__logo-band">
              <CcHeroLogoBand caption={logoBandCaption} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
